import { useState, useEffect, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Warning, Package, CaretDown, CaretUp, Prohibit } from "@phosphor-icons/react";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Incidencias() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDefectClient, setExpandedDefectClient] = useState(null);
  const [expandedCustomClient, setExpandedCustomClient] = useState(null);
  const [expandedDiscontinuedClient, setExpandedDiscontinuedClient] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  const clientsWithDefects = stats?.clients?.with_defects || [];
  const clientsWithCustomPanels = stats?.clients?.with_custom_panels || [];
  const clientsWithDiscontinued = stats?.clients?.with_discontinued || [];

  // Calculate total panels for each category
  const totalDefectPanels = clientsWithDefects.reduce((sum, c) => sum + (c.defect_count || 0), 0);
  const totalDiscontinuedPanels = clientsWithDiscontinued.reduce((sum, c) => sum + (c.discontinued_count || 0), 0);
  const totalCustomPanels = clientsWithCustomPanels.reduce((sum, c) => sum + (c.custom_panel_count || 0), 0);

  // Get total values from API response or calculate
  const totalDefectValue = stats?.clients?.total_defect_value || clientsWithDefects.reduce((sum, c) => sum + (c.defect_value || 0), 0);
  const totalDiscontinuedValue = stats?.clients?.total_discontinued_value || clientsWithDiscontinued.reduce((sum, c) => sum + (c.discontinued_value || 0), 0);
  const totalCustomValue = clientsWithCustomPanels.reduce((sum, c) => sum + (c.custom_panel_value || 0), 0);

  const toggleDefectClient = (clientId) => {
    setExpandedDefectClient(expandedDefectClient === clientId ? null : clientId);
  };

  const toggleCustomClient = (clientId) => {
    setExpandedCustomClient(expandedCustomClient === clientId ? null : clientId);
  };

  const toggleDiscontinuedClient = (clientId) => {
    setExpandedDiscontinuedClient(expandedDiscontinuedClient === clientId ? null : clientId);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidencias-page">
      {/* Header */}
      <PageHeader
        icon={Warning}
        title="Incidências"
        subtitle="Painéis com defeito, descontinuados e fora de catálogo"
      />

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Defects */}
        <div className="gresart-card">
          <div className="p-4 border-b border-stone-100 bg-amber-50/50">
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Warning size={20} className="text-amber-500" />
              Clientes com painéis com defeitos
            </h2>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-sm text-amber-600">{clientsWithDefects.length} clientes</p>
              <span className="text-sm font-bold text-amber-700">• {totalDefectPanels} painéis</span>
              <span className="text-sm font-bold text-amber-800">• €{totalDefectValue.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {clientsWithDefects.length === 0 ? (
              <p className="text-stone-400 text-center py-4">Sem incidências</p>
            ) : (
              clientsWithDefects.map((client) => (
                <div key={client.id}>
                  <button
                    onClick={() => toggleDefectClient(client.id)}
                    className="w-full flex items-center justify-between p-3 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-medium text-stone-900 truncate">{client.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">
                        {client.defect_count}
                      </span>
                      {expandedDefectClient === client.id ? (
                        <CaretUp size={16} className="text-amber-500" />
                      ) : (
                        <CaretDown size={16} className="text-amber-500" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Defect Details */}
                  {expandedDefectClient === client.id && client.defects && (
                    <div className="mt-2 ml-4 p-3 bg-white border border-amber-100 rounded-lg space-y-2">
                      {client.defects.map((defect, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="font-mono font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                            {defect.product_code}
                          </span>
                          {defect.panel_size && (
                            <span className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded text-xs">
                              {defect.panel_size}
                            </span>
                          )}
                          <span className="text-stone-500 truncate">— {defect.defect_note}</span>
                        </div>
                      ))}
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="mt-2 text-xs text-[#009392] hover:underline"
                      >
                        Ver ficha do cliente →
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Column 2: Discontinued PPM Products */}
        <div className="gresart-card">
          <div className="p-4 border-b border-stone-100 bg-stone-100/50">
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Prohibit size={20} className="text-stone-700" />
              Painéis PPM descontinuados
            </h2>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-sm text-stone-600">{clientsWithDiscontinued.length} clientes</p>
              <span className="text-sm font-bold text-stone-800">• {totalDiscontinuedPanels} painéis</span>
              <span className="text-sm font-bold text-stone-900">• €{totalDiscontinuedValue.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {clientsWithDiscontinued.length === 0 ? (
              <p className="text-stone-400 text-center py-4">Sem incidências</p>
            ) : (
              clientsWithDiscontinued.map((client) => (
                <div key={client.id}>
                  <button
                    onClick={() => toggleDiscontinuedClient(client.id)}
                    className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 rounded-lg border border-stone-300 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-medium text-stone-900 truncate">{client.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-2 py-0.5 bg-stone-800 text-white text-xs font-bold rounded">
                        {client.discontinued_count}
                      </span>
                      {expandedDiscontinuedClient === client.id ? (
                        <CaretUp size={16} className="text-stone-600" />
                      ) : (
                        <CaretDown size={16} className="text-stone-600" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Discontinued Details */}
                  {expandedDiscontinuedClient === client.id && client.discontinued_products && (
                    <div className="mt-2 ml-4 p-3 bg-white border border-stone-200 rounded-lg space-y-2">
                      {client.discontinued_products.map((product, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="font-mono font-medium text-stone-800 bg-stone-200 px-2 py-0.5 rounded">
                            {product.product_code}
                          </span>
                          {product.panel_size && (
                            <span className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded text-xs">
                              {product.panel_size}
                            </span>
                          )}
                          {product.collection && (
                            <span className="text-stone-500 text-xs">{product.collection}</span>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="mt-2 text-xs text-[#009392] hover:underline"
                      >
                        Ver ficha do cliente →
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Column 3: Custom Panels (Painéis Fora de Catálogo) */}
        <div className="gresart-card">
          <div className="p-4 border-b border-stone-100 bg-purple-50/50">
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Package size={20} className="text-purple-500" />
              PAINEIS FORA DE CATALOGO
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-purple-600">{clientsWithCustomPanels.length} clientes</p>
              <span className="text-sm font-bold text-purple-700">• {totalCustomPanels} painéis</span>
            </div>
          </div>
          <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {clientsWithCustomPanels.length === 0 ? (
              <p className="text-stone-400 text-center py-4">Sem incidências</p>
            ) : (
              clientsWithCustomPanels.map((client) => (
                <div key={client.id}>
                  <button
                    onClick={() => toggleCustomClient(client.id)}
                    className="w-full flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-medium text-stone-900 truncate">{client.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-2 py-0.5 bg-purple-500 text-white text-xs font-bold rounded">
                        {client.custom_panel_count}
                      </span>
                      {expandedCustomClient === client.id ? (
                        <CaretUp size={16} className="text-purple-500" />
                      ) : (
                        <CaretDown size={16} className="text-purple-500" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded - Link to client */}
                  {expandedCustomClient === client.id && (
                    <div className="mt-2 ml-4 p-3 bg-white border border-purple-100 rounded-lg">
                      <p className="text-sm text-stone-600 mb-2">
                        {client.custom_panel_count} painéis fora de catálogo
                      </p>
                      <button
                        onClick={() => navigate(`/clients/${client.id}`)}
                        className="text-xs text-[#009392] hover:underline"
                      >
                        Ver ficha do cliente →
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Empty State - All columns empty */}
      {clientsWithDefects.length === 0 && clientsWithCustomPanels.length === 0 && clientsWithDiscontinued.length === 0 && (
        <div className="gresart-card p-12 text-center">
          <Warning size={48} className="text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-900 mb-2">Sem incidências</h3>
          <p className="text-stone-500">Não há painéis com defeito, descontinuados ou fora de catálogo</p>
        </div>
      )}
    </div>
  );
}
